import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { AuthModel, InactivityService, PermissionDeniedError } from "@intocare/auth-data";
import { AWSAuthService, MustEnableMFAError, MustSetMainFunctionError, UnknownProblemError } from "@intocare/aws";
import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";

@Injectable()
export class AuthenticatedGuard implements CanActivate {
	private router = inject(Router);
	private awsAuthService = inject(AWSAuthService);
	private inactivityService = inject(InactivityService);
	private authModel = inject(AuthModel);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		return of(route.root.firstChild.params as { org: string }).pipe(
			filter((params) => Boolean(params.org)),
			map((params) => params.org.toLowerCase()),
			switchMap((organisation) => {
				const isExpired = this.inactivityService.isExpired(this.environment.EXPIRED_TIMEOUT);

				return this.authModel.initialize(organisation, isExpired).pipe(map(() => organisation));
			}),
			map(() => {
				if (!this.awsAuthService.isAuthenticated()) {
					// Not signed in so redirect to the auth page
					return this.router.createUrlTree(["select-organisation"], {
						queryParams: { redirectTo: state.url, ...route.queryParams },
					});
				}

				const user = this.awsAuthService.getUser();
				Sentry.setUser({ id: user.getUsername() });

				return true;
			}),
			catchError((error: unknown) => {
				if (error instanceof PermissionDeniedError) {
					const organisationParams = [
						route.root.firstChild.paramMap.get("org"),
						route.root.firstChild.paramMap.get("home"),
					].filter(Boolean);
					if (route.url.some((segment) => segment.path === "no-permissions")) {
						// If we are already on the no-permissions page, we do not need to redirect again
						return of(true);
					}
					return of(this.router.createUrlTree([...organisationParams, "no-permissions"]));
				}

				if (error instanceof MustEnableMFAError) {
					const organisationParams = [
						route.root.firstChild.paramMap.get("org"),
						route.root.firstChild.paramMap.get("home"),
					].filter(Boolean);
					if (route.url.some((segment) => segment.path === "mfa-setup")) {
						// If we are already on the mfa-setup page, we do not need to redirect again
						return of(true);
					}
					return of(this.router.createUrlTree([...organisationParams, "mfa-setup"]));
				}

				if (error instanceof MustSetMainFunctionError) {
					// If we are already on the must-set-main-function page, we do not need to redirect again
					if (route.url.some((segment) => segment.path === "must-set-main-function")) {
						return of(true);
					}
					return of(this.router.createUrlTree(["must-set-main-function"]));
				}

				if (error instanceof UnknownProblemError) {
					// If we are already on the unknown-problem page, we do not need to redirect again
					if (route.url.some((segment) => segment.path === "unknown-problem")) {
						return of(true);
					}
					return of(this.router.createUrlTree(["unknown-problem"]));
				}

				return of(false);
			}),
		);
	}
}

@Injectable()
export class NotAuthenticatedGuard implements CanActivate {
	private awsAuthService = inject(AWSAuthService);
	private router = inject(Router);
	private authModel = inject(AuthModel);
	private inactivityService = inject(InactivityService);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);

	canActivate(route: ActivatedRouteSnapshot) {
		return of(route.root.firstChild.params as { org: string }).pipe(
			filter((params) => Boolean(params.org)),
			map((params) => params.org.toLowerCase()),
			switchMap((organisation) => {
				const isExpired = this.inactivityService.isExpired(this.environment.EXPIRED_TIMEOUT);

				return this.authModel.initialize(organisation, isExpired).pipe(map(() => organisation));
			}),
			map((organisation) => {
				if (this.awsAuthService.isAuthenticated()) {
					// Already signed in so redirect to the home page
					return this.router.createUrlTree([organisation], { queryParamsHandling: "preserve" });
				}

				return true;
			}),
		);
	}
}

@Injectable()
export class IsAuthenticatedGuard implements CanActivate {
	private awsAuthService = inject(AWSAuthService);

	canActivate() {
		return this.awsAuthService.isAuthenticated();
	}
}
